import type { HTMLAttributes, ReactNode } from 'react';
import { ChevronRight } from 'lucide-react';
import { cn } from '@/lib/cn';

type PanelTone = 'default' | 'tint' | 'brand';

export interface PanelProps extends HTMLAttributes<HTMLDivElement> {
  tone?: PanelTone;
  /** Панель-кнопка: добавляет отклик на нажатие и роль button. */
  interactive?: boolean;
}

const tones: Record<PanelTone, string> = {
  default: 'bg-surface border-line/70 shadow-panel',
  tint: 'bg-soft border-transparent',
  brand: 'border-transparent text-white bg-[linear-gradient(145deg,#55438D,#3B2F63)]',
};

/** Базовая карточка-поверхность. */
export function Panel({ tone = 'default', interactive, className, children, ...rest }: PanelProps) {
  return (
    <div
      className={cn(
        'rounded-panel border p-3.5',
        tones[tone],
        interactive && 'cursor-pointer transition-transform active:scale-[.99]',
        className,
      )}
      {...(interactive ? { role: 'button', tabIndex: 0 } : {})}
      {...rest}
    >
      {children}
    </div>
  );
}

export interface RowProps {
  icon?: ReactNode;
  title: ReactNode;
  subtitle?: ReactNode;
  trailing?: ReactNode;
  /** Показать шеврон вместо произвольного trailing. */
  chevron?: boolean;
  onClick?: () => void;
  className?: string;
  align?: 'center' | 'start';
}

/** Строка списка: иконка, две строки текста и правый элемент. */
export function Row({
  icon,
  title,
  subtitle,
  trailing,
  chevron,
  onClick,
  className,
  align = 'center',
}: RowProps) {
  const Tag = onClick ? 'button' : 'div';
  return (
    <Tag
      {...(onClick ? { type: 'button' as const, onClick } : {})}
      className={cn(
        'flex w-full items-center justify-between gap-2.5 text-left',
        onClick && 'bd-focus rounded-xl transition-transform active:scale-[.99]',
        className,
      )}
    >
      <span className={cn('flex min-w-0 gap-2.5', align === 'center' ? 'items-center' : 'items-start')}>
        {icon}
        <span className="min-w-0">
          <span className="block truncate text-[13px] font-medium leading-snug">{title}</span>
          {subtitle ? (
            <span className="mt-0.5 block text-[11px] leading-[1.35] text-muted">{subtitle}</span>
          ) : null}
        </span>
      </span>
      {chevron ? <ChevronRight className="h-4 w-4 shrink-0 text-muted" aria-hidden /> : trailing}
    </Tag>
  );
}

/**
 * Список строк с разделителями внутри одной панели.
 * Каждый элемент получает одинаковые внутренние отступы.
 */
export function RowList({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <Panel className={cn('p-0', className)}>
      <div className="divide-y divide-line/70 [&>*]:px-3.5 [&>*]:py-3">{children}</div>
    </Panel>
  );
}

type IconTone = 'brand' | 'lime' | 'soft' | 'muted';

const iconTones: Record<IconTone, string> = {
  brand: 'bg-brand text-black',
  lime: 'bg-brand-lime text-black',
  soft: 'bg-soft text-accent',
  muted: 'bg-surface-2 text-muted',
};

export function MiniIcon({
  children,
  tone = 'lime',
  size = 34,
  className,
}: {
  children: ReactNode;
  tone?: IconTone;
  size?: number;
  className?: string;
}) {
  return (
    <span
      className={cn('flex shrink-0 items-center justify-center rounded-xl', iconTones[tone], className)}
      style={{ width: size, height: size }}
      aria-hidden
    >
      {children}
    </span>
  );
}

type BadgeTone = 'default' | 'success' | 'muted' | 'warning';

const badgeTones: Record<BadgeTone, string> = {
  default: 'bg-brand-lime text-black',
  success: 'bg-brand-lime text-black',
  warning: 'bg-soft text-accent',
  muted: 'bg-surface-2 text-muted',
};

export function Badge({
  children,
  tone = 'default',
  className,
}: {
  children: ReactNode;
  tone?: BadgeTone;
  className?: string;
}) {
  return (
    <span
      className={cn(
        'inline-flex shrink-0 items-center gap-1 whitespace-nowrap rounded-full px-2 py-1 text-[10px] font-medium',
        badgeTones[tone],
        className,
      )}
    >
      {children}
    </span>
  );
}

export function ProgressBar({ value, tone = 'brand' }: { value: number; tone?: 'brand' | 'lime' }) {
  const clamped = Math.max(0, Math.min(100, Math.round(value)));
  return (
    <div
      className={cn('h-[7px] overflow-hidden rounded-full', tone === 'lime' ? 'bg-white/25' : 'bg-muted/20')}
      role="progressbar"
      aria-valuenow={clamped}
      aria-valuemin={0}
      aria-valuemax={100}
    >
      <div
        className={cn(
          'h-full rounded-full transition-[width] duration-500',
          tone === 'lime' ? 'bg-brand-lime' : 'bg-brand',
        )}
        style={{ width: `${clamped}%` }}
      />
    </div>
  );
}

export function Kicker({ children }: { children: ReactNode }) {
  return (
    <div className="mb-1.5 text-[11px] font-medium uppercase tracking-[0.04em] text-accent">
      {children}
    </div>
  );
}

export function PageTitle({ children, className }: { children: ReactNode; className?: string }) {
  return <h1 className={cn('mb-2 text-display', className)}>{children}</h1>;
}

export function Lead({ children, className }: { children: ReactNode; className?: string }) {
  return <p className={cn('mb-4 text-[13px] leading-[1.45] text-muted', className)}>{children}</p>;
}

export function Helper({ children, error }: { children: ReactNode; error?: boolean }) {
  return (
    <p className={cn('mt-1.5 text-[11px] leading-[1.4]', error ? 'text-accent' : 'text-muted')}>
      {children}
    </p>
  );
}

export function Meta({ children, className }: { children: ReactNode; className?: string }) {
  return <span className={cn('text-[11px] text-muted', className)}>{children}</span>;
}

export function SectionHeading({ children, action }: { children: ReactNode; action?: ReactNode }) {
  return (
    <div className="mb-2 mt-4 flex items-center justify-between gap-2 px-0.5">
      <h2 className="text-[17px] font-medium tracking-[-0.02em]">{children}</h2>
      {action}
    </div>
  );
}

/** Номер в квадрате: порядковый знак урока в списке. */
export function CircleNumber({
  children,
  tone = 'brand',
  size = 28,
}: {
  children: ReactNode;
  tone?: 'brand' | 'done' | 'locked';
  size?: number;
}) {
  return (
    <span
      className={cn(
        'flex shrink-0 items-center justify-center rounded-[10px] text-[11px] font-medium',
        tone === 'brand' && 'bg-brand text-black',
        tone === 'done' && 'bg-brand-lime text-black',
        tone === 'locked' && 'bg-soft text-muted',
      )}
      style={{ width: size, height: size }}
    >
      {children}
    </span>
  );
}
